import { isAllCitiesSelection } from '../../helpers/allCitiesOfCountry';
import { normalizeContactDigits } from '../../helpers/organizationAutofill';
import {
  CITY_MODES,
  CONTACT_FIELDS,
  DAY_KEYS,
  emptySchedule,
} from '../../helpers/organizationsBulkAutofill';

export const FIELD_TYPES = {
  text: 'text',
  textarea: 'textarea',
  contact: 'contact',
  category: 'category',
  subCategories: 'subCategories',
  city: 'city',
  additionalCities: 'additionalCities',
  address: 'address',
  coordinates: 'coordinates',
  schedule: 'schedule',
  languages: 'languages',
  tags: 'tags',
  templatePhotos: 'templatePhotos',
};

/**
 * Все поля предложения, доступные для редактирования — и по одной карточке,
 * и сразу у выбранных. Порядок такой же, как на экране создания предложения.
 */
export const OFFER_FIELDS = [
  { key: 'ownerId', label: 'ID владельца', type: FIELD_TYPES.text },
  { key: 'name', label: 'Название', type: FIELD_TYPES.text },
  { key: 'category', label: 'Категория', type: FIELD_TYPES.category },
  { key: 'subCategories', label: 'Подкатегории', type: FIELD_TYPES.subCategories },
  { key: 'specialization', label: 'Специализация', type: FIELD_TYPES.text },
  { key: 'city', label: 'Город', type: FIELD_TYPES.city },
  { key: 'additionalCities', label: 'Дополнительные города', type: FIELD_TYPES.additionalCities },
  { key: 'address', label: 'Адрес', type: FIELD_TYPES.address },
  { key: 'coordinates', label: 'Координаты', type: FIELD_TYPES.coordinates },
  { key: 'schedule', label: 'График работы', type: FIELD_TYPES.schedule },
  ...CONTACT_FIELDS.map((contact) => ({
    key: `contacts.${contact.key}`,
    label: contact.label,
    type: FIELD_TYPES.contact,
    contactKey: contact.key,
    digitsOnly: contact.digitsOnly,
  })),
  { key: 'description', label: 'Описание', type: FIELD_TYPES.textarea },
  { key: 'languages', label: 'Языки', type: FIELD_TYPES.languages },
  { key: 'tags', label: 'Тэги', type: FIELD_TYPES.tags },
  { key: 'templatePhotoIds', label: 'Шаблонные фото', type: FIELD_TYPES.templatePhotos },
];

export function getFieldByKey(key) {
  return OFFER_FIELDS.find((field) => field.key === key) || null;
}

/** Зафиксированные в настройках поля редактировать нельзя: они применяются ко всем. */
export function isFieldLocked(field, config) {
  if (!field) return false;
  switch (field.key) {
    case 'ownerId':
      return true;
    case 'category':
      return Boolean(config?.category);
    case 'subCategories':
      return Boolean(config?.category && config?.subCategory);
    case 'city':
      return Boolean(config?.country) && config?.cityMode !== CITY_MODES.perOffer;
    default:
      return false;
  }
}

/** Поля, которые не имеют смысла для конкретного черновика (онлайн-категория, все города). */
export function isFieldIrrelevant(field, draft) {
  if (!field || !draft) return false;
  const isOnline = Boolean(draft.category?.is_online);
  const isInAllCities = isAllCitiesSelection(draft.city);

  const locationFields = ['city', 'additionalCities', 'address', 'coordinates'];
  if (isOnline && locationFields.includes(field.key)) return true;
  if (isInAllCities && ['additionalCities', 'address', 'coordinates'].includes(field.key)) return true;
  if (field.key === 'subCategories' && !draft.category?.sub_categories?.length) return true;

  return false;
}

export function getFieldValue(draft, field) {
  if (!draft || !field) return undefined;
  if (field.type === FIELD_TYPES.contact) {
    return draft.contacts?.[field.contactKey] || '';
  }
  return draft[field.key];
}

/**
 * Значение поля → патч черновика с учётом связей между полями
 * (категория сбрасывает подкатегории, онлайн-категория убирает локацию и т.д.).
 */
export function applyFieldValue(draft, field, value, extra = null) {
  if (!field) return draft;

  let patch = {};

  switch (field.type) {
    case FIELD_TYPES.contact: {
      const clean = field.digitsOnly ? normalizeContactDigits(value) : String(value ?? '').trim();
      const contacts = { ...(draft.contacts || {}) };
      if (clean) {
        contacts[field.contactKey] = clean;
      } else {
        delete contacts[field.contactKey];
      }
      patch = { contacts };
      break;
    }

    case FIELD_TYPES.category: {
      patch = { category: value || null, subCategories: [] };
      if (value?.is_online) {
        patch.city = null;
        patch.additionalCities = [];
        patch.address = '';
        patch.coordinates = null;
      }
      break;
    }

    case FIELD_TYPES.city: {
      patch = { city: value || null };
      if (isAllCitiesSelection(value)) {
        patch.additionalCities = [];
        patch.address = '';
        patch.coordinates = null;
      } else if (value?.country_id !== draft.city?.country_id) {
        patch.additionalCities = [];
      }
      break;
    }

    case FIELD_TYPES.schedule: {
      patch = { schedule: { ...emptySchedule(), ...(value || {}) } };
      break;
    }

    default: {
      patch = { [field.key]: value };
      break;
    }
  }

  return { ...draft, ...patch, ...(extra || {}) };
}

export function scheduleDayLabel(day) {
  const dayValue = day?.type;
  if (!dayValue) return '—';
  if (dayValue === 'hours') return `${day.start}-${day.end}`;
  if (dayValue === 'day_off') return 'выходной';
  if (dayValue === 'temp_closed') return 'врем. закрыто';
  if (dayValue === 'whole_day') return 'круглосуточно';
  return dayValue;
}

export function scheduleShortText(schedule) {
  if (!schedule) return '—';
  return DAY_KEYS
    .map((day) => scheduleDayLabel(schedule[day]))
    .join(' · ');
}
